import {
  StyleSheet,
  View,
  type PressableProps,
  type StyleProp,
  type ViewStyle,
} from "react-native";

import type { IconName } from "./Icon/Icon";
import { IconButton, type IconButtonVariant } from "./primitives/IconButton";
import type { UiTheme } from "./theme";

// <TimerControlsRow> — the fan of timer commands, and nothing else. Extracted
// out of <TimerBanner> (MX-5 slice 3), where it was the only place this row
// existed: the hold timer's commands now live on the effort meter's AXIS row,
// not on a banner of their own, so the same fan has a second real callsite and
// the layout rules that make it safe (the touch expansion, the extra gap before
// the destructive ✕) must stay written once. A second hand-rolled fan would
// have drifted from the banner's the first time one of them was tuned.
//
// Presentational, hook-free, i18n-free: the caller builds the model. What it
// does NOT own is the surface underneath — the banner paints itself brand and
// asks for `primary` controls, the axis row is flat and asks for `default`
// ones. Tone stays per-control, so one row primitive serves both.

/** A control's tone. On the brand banner the default is a brand-filled
 *  `primary`; on the flat axis row the caller passes `default`. The ✕ is the
 *  muted `cancel` in both. */
export type TimerControlVariant = Extract<
  IconButtonVariant,
  "primary" | "cancel" | "default"
>;

type TimerControlBase = {
  key: string;
  /** Screen-reader label — the icon is never the only signal. */
  a11yLabel: string;
  onPress: () => void;
  variant?: TimerControlVariant;
  disabled?: boolean;
};

export type TimerControl = TimerControlBase &
  (
    | { icon: IconName; label?: never }
    /** A step control says its AMOUNT (`−10`, `+30`) instead of carrying a bare
     *  ∓ glyph: the step differs per timer, so the number is the information. */
    | { icon?: never; label: string }
  );

// Symmetric ≥44pt touch expansion, matching the pre-lift banner's HIT_SLOP=8
// (restored after the Fase 2 lift briefly dropped horizontal slop to 36pt).
// Adjacent same-tone buttons' zones overlap slightly at the s2 gap — accepted
// pre-lift; the one adjacency that mattered (✓ next to ✕, inbox 9b5daf1a
// "Antenore kept hitting ✕ instead of ✓") is kept spatially distinct by the
// extra leading gap on the cancel control below, not by shrinking the target.
const ROW_HIT_SLOP = 8;

export function TimerControlsRow({
  theme,
  controls,
  defaultVariant = "primary",
  hitSlop = ROW_HIT_SLOP,
  style,
}: {
  theme: UiTheme;
  controls: TimerControl[];
  /** Tone for controls that don't name one. `primary` on a brand banner,
   *  `default` on a flat row. */
  defaultVariant?: TimerControlVariant;
  /** Touch expansion per control. The default is symmetric 8 — right for the
   *  banner, where the row is short and wide. A DENSER row (the effort meter's
   *  axis, five controls beside a clock) pairs a wider gap with a smaller
   *  HORIZONTAL slop, so neighbouring zones meet instead of overlapping: with
   *  gap 8 and slop 4 every control is a clean 44 wide and a near-boundary tap
   *  can't flip the direction when it meant to add ten seconds (a11y audit
   *  2026-07-22). Vertical slop stays generous — nothing sits above or below. */
  hitSlop?: PressableProps["hitSlop"];
  /** Layout-only override from the host (e.g. the axis row's flex share). */
  style?: StyleProp<ViewStyle>;
}) {
  const styles = makeStyles(theme);
  return (
    <View style={[styles.actions, style]}>
      {controls.map((c) => {
        const common = {
          theme,
          accessibilityLabel: c.a11yLabel,
          onPress: c.onPress,
          variant: c.variant ?? defaultVariant,
          disabled: c.disabled,
          hitSlop,
          // Extra leading gap before the destructive ✕ so it never sits flush
          // against the confirm/skip button next to it (inbox 9b5daf1a).
          style: c.variant === "cancel" ? styles.cancelGap : undefined,
        };
        return c.label !== undefined ? (
          <IconButton key={c.key} {...common} label={c.label} />
        ) : (
          <IconButton key={c.key} {...common} name={c.icon} />
        );
      })}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { spacing: s } = theme;
  return StyleSheet.create({
    actions: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: s.s2,
      rowGap: s.s2,
      // Let the row shrink below content width so flexWrap can engage (RN's
      // flexShrink:0 default otherwise pushes the last button off-screen on
      // narrow widths / larger fonts).
      flexShrink: 1,
      minWidth: 0,
    },
    // Restores the pre-lift cancel-button separation (was restBannerCancelButton
    // marginLeft): keeps the ✕ spatially apart from the adjacent confirm/skip.
    cancelGap: {
      marginLeft: s.s3,
    },
  });
}
